<script setup lang="ts">
import Product from "@/product/interfaces/Product";
import {pluralize} from "@/core/filters/Pluralize";
import AddProductButton from "@/product/components/AddProductButton.vue";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/core/components/ui/dialog";
import {ref, watch} from "vue";
import draggable from 'vuedraggable';
import Icon from "@/core/components/icon/Icon.vue";
import {medicineCabinetApi} from "@/container";

const {categoryId, featuredProducts} = defineProps<{ categoryId: number; featuredProducts: Product[] }>();
defineEmits<{ (e: 'productSelected', payload: number[]): void; }>();

const products = ref<Product[]>(featuredProducts);

watch(() => featuredProducts, () => {
    products.value = featuredProducts;
});

watch(products, () => {
    onChange(products.value);
});

/**
 * Action when the list is updated.
 *
 * @param {Product[]} data Updated data list.
 *
 * @return {Promise<void>}
 */
const onChange = (data: Product[]) => {
    const itemIds = data.map(item => item.id);

    return medicineCabinetApi().orderProducts(categoryId, itemIds);
};

/**
 * Action when an item of the list should be deleted.
 *
 * @param {Product} item Item to delete.
 *
 * @return {Promise<void>}
 */
const onDelete = (item: Product): Promise<void> => {
    return medicineCabinetApi().deleteProduct(categoryId, item.id)
        .then(() => {
            if (products.value) {
                const index = products.value?.indexOf(item);
                if (index !== -1) {
                    const array = [...products.value];
                    array.splice(index, 1);
                    products.value = array;
                }
            }
        });
};
</script>

<template>
  <div class="bg-white p-4">
    <h3>Produits conseillés</h3>
    <p>Ces produits s'afficheront sur l'application Apodis de vos patients.</p>
    <Dialog>
      <DialogTrigger as-child>
        <p v-if="products.length" class="text-primary font-semibold hover:opacity-75 cursor-pointer">
          {{ products.length }} {{ pluralize("produit", products.length) }} dans la sélection >
        </p>
      </DialogTrigger>
      <DialogContent
        class="flex flex-col lg:max-w-screen-lg overflow-y-scroll max-h-screen"
        @interact-outside="(e) => e.preventDefault()"
      >
        <DialogHeader class="p-0">
          <DialogTitle>
            Produits séléctionnés
          </DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <h3>Réordonnez les produits en maintenant le clic sur un produit + icone de grip</h3>
          <draggable v-model="products" item-key="product">
            <template #item="{ element: product }">
              <div
                class="flex flex-row gap-4 justify-between align-center cursor-move bg-white shadow-lg p-4 rounded-lg my-2">
                <div class="flex flex-row">
                  <Icon name="mdi-drag" :size="22" class="text-primary" />
                  <p>{{ product.name }}</p>
                </div>
                <Icon
                  name="mdi-trash-can"
                  :size="22"
                  lass="text-gray-dark cursor-pointer hover:opacity-75"
                  @click="() => onDelete(product)" />
              </div>
            </template>
          </draggable>
        </div>
      </DialogContent>
    </Dialog>
    <div class="flex justify-end">
      <AddProductButton @confirm="productSelected => $emit('productSelected', productSelected)" />
    </div>
  </div>
</template>
